import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import { UserRolesFacade } from '@app/components/user-roles/user-roles.facade';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OutstandingItemsModalComponent } from '@app/shared/common/outstanding-items-modal/outstanding-items-modal.component';
import {
  CheckCanChangeUserRoleInput,
  CheckCanChangeUserRoleOutput,
  ContactDetailsDto,
  GetStaffUserDetailsForEditOutput,
  ListResultDtoOfRoleListDto,
  RoleServiceProxy,
  StaffsAndUsersServiceProxy,
} from '@shared/service-proxies/service-proxies';
import { ChangeUserRolesModalComponent } from '@app/shared/common/change-user-roles-modal/change-user-roles-modal.component';

@Component({
  selector: 'app-user-roles',
  templateUrl: './user-roles.component.html',
  providers: [UserRolesFacade],
})
export class UserRolesComponent extends AppComponentBase implements OnInit {
  @Input() staff: GetStaffUserDetailsForEditOutput;
  @Input() ownDetailsEdit: Boolean;
  @Output() roleChanged: EventEmitter<any> = new EventEmitter<any>();

  saving = false;

  userRolesForm: FormGroup;
  submitted = false;

  @ViewChild('outstandingItemsModal', { static: true })
  outstandingItemsModal: OutstandingItemsModalComponent;

  @ViewChild('changeUserRolesModal', { static: true })
  changeUserRolesModal: ChangeUserRolesModalComponent;

  staffRoleArray : {
    id: number;
    name: string;
    displayName: string;
  }[];

  constructor(
    injector: Injector,
    private facade: UserRolesFacade,
    private formBuilder: FormBuilder,
    private _staffAndUsersServiceProxy: StaffsAndUsersServiceProxy,
    private _roleService: RoleServiceProxy
  ) {
    super(injector);
  }

  ngOnInit() {
    this.userRolesForm = this.formBuilder.group({
      staffRole: [null, [Validators.required]],
    });

    if (this.staff) {
      this.userRolesForm.patchValue({
        staffRole: this.staff.staffUserRole.staffRoleId
      });

      if(!this.appSession.tenant.isManualUserCreation){
        // ProjectAdministrator validation
        if (this.staff.staffUserRole.staffRoleName === "default.projectadministration") {
          this.userRolesForm.controls.staffRole.disable();
        }
      }

      this._roleService.getRoles(null,null,undefined,undefined)
      .subscribe((roles: ListResultDtoOfRoleListDto) => {
        this.staffRoleArray = roles.items.map(r => ({id: r.id, name: r.name, displayName: r.displayName}));
      });
    }
  }

  saveData() {
    this.saving = true;
    let params = { ...this.staff };

    let roleId = Number(
      this.userRolesForm.controls.staffRole.value
    );
    params.staffUserRole.staffRoleId = roleId;
    params.staffUserRole.staffRoleName = this.getRoleName(roleId);
    params.staffUserRole.staffRoleDisplayName = this.getRoleDisplayName(roleId);
    return this.facade.updateStaffUserRoleDetails(params);
  }

  save(): void {
    if (this.checkIfStaffRoleWasChanged()) {
      this.saveActionForStaffRoleChanged();
    } else {
      this.finalSave();
    }
  }

  showChangeUserRolesModal(
    staff: ContactDetailsDto,
    oldRole: string,
    newRole: string
  ): void {
    this.changeUserRolesModal.show({ staff, oldRole, newRole });
  }

  showOutstandingItemsModal(
    staff: GetStaffUserDetailsForEditOutput,
    checkOutput: CheckCanChangeUserRoleOutput,
    isRoleChange: boolean
  ): void {
    this.outstandingItemsModal.show({ checkOutput, staff, isRoleChange });
  }

  changeRole(): void {
    this.finalSave();
  }

  onSubmit() {
    this.submitted = true;

    if (this.userRolesForm.invalid) {
      return;
    }

    return this.save();
  }

  private getRoleName(roleId: number): string {
    const role = this.staffRoleArray.find(
      (item) => item.id === roleId
    );

    return role ? role.name : 'Empty';
  }

  private getRoleDisplayName(roleId: number): string {
    const role = this.staffRoleArray.find(
      (item) => item.id == roleId
    );

    return role ? role.displayName : 'Empty';
  }

  private saveActionForStaffRoleChanged(): void {
    const body = new CheckCanChangeUserRoleInput();
    body.userId = this.staff.userId;
    this._staffAndUsersServiceProxy
      .checkCanChangeUserRole(body)
      .subscribe((checkOutput: CheckCanChangeUserRoleOutput) => {
        if (checkOutput.canChangeRole) {
          this.showChangeUserRolesModal(
            this.staff.contactDetails,
            this.getRoleDisplayName(this.staff.staffUserRole.staffRoleId),
            this.getRoleDisplayName(this.userRolesForm.controls.staffRole.value)
          );
        } else {
          this.showOutstandingItemsModal(this.staff, checkOutput, true);
        }
      });
  }

  private finalSave(): void {
    this.saveData().subscribe(
      (x) => {
        this.notify.success(this.l('SuccessfullySavedRole'));
        this.roleChanged.emit(true);

        this.saving = false;
        if(x.roleChangedToSupervisor === true){
          setTimeout(() => { location.reload()}, 0);
        }
      },
      (err) => {
        this.saving = false;
      }
    );
  }

  private checkIfStaffRoleWasChanged(): boolean {
    return (
      Number(this.staff.staffUserRole.staffRoleId) !==
      Number(this.userRolesForm.controls.staffRole.value)
    );
  }
}
